import * as Sentry from "@sentry/react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useAuth } from "@/services/auth/auth-context";
import { useMyClientData } from "@/services/entities/use-users";
import { CLIENT_ENGAGEMENT_VERSION } from "@/utils/constants";

export type ClientAccessStatus =
  | "loading"
  | "ready"
  | "needs-onboarding"
  | "needs-engagement"
  | "inactive"
  | "staff-warning";

export interface UseClientAccessGuardResult {
  /** Current access status */
  status: ClientAccessStatus;
  /** Client data (available when status is 'ready', 'needs-engagement', or 'inactive') */
  client: ReturnType<typeof useMyClientData>["client"];
  /** Whether client data is still loading */
  isLoading: boolean;
}

/**
 * Hook that checks client access status and handles redirects.
 *
 * This centralizes all the "can this user access the client dashboard?" logic:
 * 1. Is user authenticated? (handled by ProtectedRoute in layout)
 * 2. Does user have a client record? (redirect to onboard if not)
 * 3. Has user signed the engagement letter? (redirect to sign if not)
 * 4. Is client account active? (show inactive notice if not)
 *
 * @param options.autoRedirect - Whether to automatically redirect (default: true)
 * @returns Current access status and client data
 */
export function useClientAccessGuard(options?: {
  autoRedirect?: boolean;
}): UseClientAccessGuardResult {
  const { autoRedirect = true } = options ?? {};
  const navigate = useNavigate();
  const location = useLocation();
  const { authUser, isStaff, isAdmin } = useAuth();
  const { client, isLoading: clientIsLoading } = useMyClientData();

  // Determine the current status
  const status = determineStatus({
    authUser,
    client,
    clientIsLoading,
    isStaff,
    isAdmin,
  });

  // Check if we're already on the correct page for the status
  const isOnOnboardPage = location.pathname.includes("/clientdashboard/onboard");
  const isOnEngagementPage = location.pathname.includes("/clientdashboard/engagement_agreement");

  // Handle redirects (only if not already on the target page)
  useEffect(() => {
    if (!autoRedirect) return;

    if (status === "needs-onboarding" && !isOnOnboardPage) {
      navigate({ to: "/clientdashboard/onboard" });
    } else if (status === "needs-engagement" && !isOnEngagementPage) {
      navigate({ to: "/clientdashboard/engagement_agreement" });
    }
  }, [status, autoRedirect, navigate, isOnOnboardPage, isOnEngagementPage]);

  // If we're on the correct page for our status, return "ready" so Outlet renders
  const effectiveStatus =
    status === "needs-onboarding" && isOnOnboardPage
      ? "ready"
      : status === "needs-engagement" && isOnEngagementPage
        ? "ready"
        : status;

  return {
    status: effectiveStatus,
    client,
    isLoading: clientIsLoading,
  };
}

function determineStatus(params: {
  authUser: ReturnType<typeof useAuth>["authUser"];
  client: ReturnType<typeof useMyClientData>["client"];
  clientIsLoading: boolean;
  isStaff: boolean;
  isAdmin: boolean;
}): ClientAccessStatus {
  const { authUser, client, clientIsLoading, isStaff, isAdmin } = params;

  // Still loading
  if (clientIsLoading && !client) {
    return "loading";
  }

  // No client record exists
  if (authUser && !client && !clientIsLoading) {
    // Staff/admin viewing client dashboard without clfient record
    if (isStaff || isAdmin) {
      Sentry.logger.info(
        `Staff/Admin user ${authUser.id} attempted to access client dashboard without client record.`,
      );
      return "staff-warning";
    }
    // Regular user needs to onboard
    Sentry.logger.info(`User ${authUser.id} has no client record; redirecting to onboarding.`);
    return "needs-onboarding";
  }

  // Client exists but is inactive
  if (client?.is_active === false) {
    Sentry.addBreadcrumb({
      message: `Inactive client ${client.user_id} attempted to access dashboard.`,
    });
    return "inactive";
  }

  // Client exists but hasn't signed engagement letter (or needs to re-sign newer version)
  if (
    client &&
    (!client.engagement_letter_signed_date ||
      (client.engagement_letter_version ?? "0.0") < CLIENT_ENGAGEMENT_VERSION)
  ) {
    Sentry.addBreadcrumb({
      message: `Client ${client.user_id} needs to sign engagement letter.`,
    });
    return "needs-engagement";
  }

  // All checks passed
  Sentry.addBreadcrumb({
    message: `Client ${client?.user_id} passed access checks.`,
  });
  return "ready";
}

/**
 * Hook for fetching upcoming events for the client dashboard.
 * Separates event fetching from access control.
 */
export { useFamilyEvents } from "@/services/entities/use-events";
